import type { ParseKeys } from "i18next";

// NOTE: NSAI-5611 — the kinds of agent the chat's `runAgentAndWait` tool can
// start. Mirrors the server's invokable-agent kinds.
export enum EAgentRunKind {
  SCHEDULED_TASK = "scheduled_task",
  WORKFLOW = "workflow",
}

// NOTE: NSAI-5611 — every status a `runAgentAndWait` result can report: the
// run's own outcome, plus the two ways the chat's wait can end before the run
// does (it outlasted the wait budget, or the turn was cancelled).
export enum EAgentRunResultStatus {
  OK = "ok",
  ERROR = "error",
  SKIPPED = "skipped",
  AWAITING_APPROVAL = "awaiting_approval",
  RUNNING = "running",
  STILL_RUNNING = "still_running",
  WAIT_ABANDONED = "wait_abandoned",
}

// NOTE: NSAI-5611 — what the server's `runAgentAndWait` chat tool hands back
// (IRunAgentAndWaitResult in chat/tools/agent-invocation). `agentKind` and
// `status` stay plain strings so a value a newer server adds still renders —
// the display helpers map the known ones and fall back for the rest. Only ever
// trust this shape after `isRunAgentAndWaitResult`: on a refusal the tool
// returns a plain string instead.
export interface IRunAgentAndWaitResult {
  agentUuid: string;
  agentName: string;
  agentKind: string;
  runUuid: string;
  status: string;
  output: string | null;
  isOutputTruncated: boolean;
  error: string | null;
  note?: string | undefined;
}

// NOTE: How strongly a status reads; each client maps it onto its own badge
// palette.
export type TAgentRunStatusTone =
  | "success"
  | "destructive"
  | "warning"
  | "neutral";

export type TAgentRunStatusLabelKey = Extract<
  ParseKeys<"chat">,
  `agent_run.status.${string}`
>;

export type TAgentRunNoteKey = Extract<
  ParseKeys<"chat">,
  `agent_run.notes.${string}`
>;

export type TAgentRunKindLabelKey = Extract<
  ParseKeys<"chat">,
  `agent_run.kind.${string}`
>;

export interface IAgentRunStatusDisplay {
  tone: TAgentRunStatusTone;
  // NOTE: null for a status this client does not know; render it verbatim.
  labelKey: TAgentRunStatusLabelKey | null;
  // NOTE: the explanatory line under the card, when the status needs one.
  noteKey: TAgentRunNoteKey | null;
}

export interface IGetAgentRunStatusDisplayProps {
  status: string;
}

export interface IGetAgentRunKindLabelKeyProps {
  kind: string;
}

export interface IGetAgentRunNoteKeysProps {
  result: IRunAgentAndWaitResult;
}

export interface IHasAgentRunHistoryLinkProps {
  kind: string;
}

export interface IReadAgentRunResultsProps {
  // NOTE: `unknown` on purpose: the field is read from a message payload the
  // server owns, so every entry is checked before it is rendered.
  message: { agentRunResults?: unknown };
}

// NOTE: NSAI-5609 — the parent-run fields a Run History row carries when
// another agent's run started it. Optional because an older server omits them.
export interface IParentRunFields {
  parentRunUuid?: string | null | undefined;
  parentAutomationUuid?: string | null | undefined;
}

// NOTE: NSAI-7883 — which table a child run lives in, mirroring the server's
// EAutomationChildRunKind: another agent's run can start a scheduled agent or
// a workflow.
export enum EAutomationChildRunKind {
  AGENT = "agent",
  WORKFLOW = "workflow",
}

// NOTE: NSAI-7883 — one run another agent's run started, as
// GET /automation/runs/:runUuid/children lists it (oldest first). Only the
// child's summary: its output and trace stay behind its own Run History.
export interface IAutomationChildRun {
  uuid: string;
  kind: EAutomationChildRunKind;
  automationUuid: string;
  automationName: string;
  status: string;
  triggerType: string | null;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
}

// NOTE: NSAI-7883 — how many runs a Run History row started. Optional because
// an older server omits it; absent and 0 both mean "none".
export interface IChildRunCountField {
  childRunCount?: number | undefined;
}

export interface IIsChildRunListTruncatedProps {
  listedCount: number;
  childRunCount: number;
}

export interface INamedAutomation {
  uuid: string;
  name: string;
}

// NOTE: NSAI-7883 — which of the Agents page's four lists a parent run's
// automation was found in, so its line names the right kind ("Started by
// workflow …"), not "agent" for every parent.
export enum EParentAutomationKind {
  AGENT = "agent",
  WORKFLOW = "workflow",
  WEBHOOK = "webhook",
  EVENT_RULE = "event_rule",
}

export interface IParentAutomationLists {
  agents: readonly INamedAutomation[] | null | undefined;
  workflows: readonly INamedAutomation[] | null | undefined;
  webhooks: readonly INamedAutomation[] | null | undefined;
  eventRules: readonly INamedAutomation[] | null | undefined;
}

export interface IResolveParentAutomationProps {
  parentAutomationUuid: string | null | undefined;
  lists: IParentAutomationLists;
}

export interface IResolvedParentAutomation {
  name: string;
  kind: EParentAutomationKind;
}

export type TParentRunLabelKey =
  | "runs.startedByAgent"
  | "runs.startedByWorkflow"
  | "runs.startedByWebhook"
  | "runs.startedByEventRule";

export interface IResolveParentAgentNameProps {
  parentAutomationUuid: string | null | undefined;
  automations: readonly INamedAutomation[] | null | undefined;
}
